import { Link, useParams } from "react-router-dom";
import { CalendarDays, Image as ImageIcon, Newspaper, Shield, Users } from "lucide-react";
import Seo from "@/components/site/Seo";
import { Crest, EmptyState, StatusPill } from "@/components/site/kit";
import { MatchCard, NewsCard, indexBy } from "@/components/site/cards";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import useFetch from "@/hooks/useFetch";
import { fileUrl } from "@/lib/api";
import NotFound from "./NotFound";

export const PlayerTile = ({ p }) => (
  <Link to={`/players/${p.id}`} data-testid={`player-tile-${p.id}`} className="card-lift block overflow-hidden rounded-md border border-white/10 bg-[#112240]">
    <div className="aspect-square bg-[#0A192F]">
      {p.photo ? <img src={fileUrl(p.photo)} alt={p.name} loading="lazy" className="h-full w-full object-cover" /> : <Users className="m-auto h-full w-12 text-slate-600" />}
    </div>
    <div className="p-4">
      <p className="font-display text-lg uppercase leading-tight">{p.name}</p>
      <p className="text-xs uppercase tracking-wider text-[#00E5FF]">{p.player_role || "Player"}</p>
    </div>
  </Link>
);

const StatBox = ({ l, v }) => (
  <div className="rounded-md border border-white/10 bg-[#0A192F] p-4">
    <p className="font-score text-3xl font-bold">{v ?? 0}</p>
    <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">{l}</p>
  </div>
);

export default function TeamDetail() {
  const { slug } = useParams();
  const { data, loading, error } = useFetch(`/teams/${slug}`);
  const { data: teams } = useFetch("/teams", []);
  const { data: venues } = useFetch("/venues", []);
  if (error) return <NotFound />;
  if (loading || !data) return <div className="min-h-screen" />;
  const { team, players, matches, standing, news, gallery } = data;
  const tMap = indexBy(teams);
  const vMap = indexBy(venues);
  const upcoming = matches.filter((m) => ["scheduled", "live"].includes(m.status));
  const results = matches.filter((m) => ["completed", "abandoned"].includes(m.status));
  const coaches = (team.coaches || "").split("\n").map((l) => l.trim()).filter(Boolean);
  const announced = team.status === "announced";

  return (
    <>
      <Seo title={team.name} description={`${team.name} — franchise of the Telangana–Andhra T10 League.`} />
      <section data-testid="team-banner" className="grain relative overflow-hidden border-b border-white/10 pb-14 pt-36">
        {team.banner ? <img src={fileUrl(team.banner)} alt={`${team.name} banner`} className="absolute inset-0 h-full w-full object-cover opacity-40" /> : <div className="stripe-bg absolute inset-0" />}
        <div className="absolute inset-0" style={{ background: `linear-gradient(110deg, #0A192F 35%, ${team.primary_color}33)` }} />
        <div className="container-x relative flex flex-col gap-8 sm:flex-row sm:items-end">
          <Crest size="lg" number={team.number} color={team.primary_color} logo={fileUrl(team.logo)} name={team.name} />
          <div>
            <StatusPill tone={announced ? "ok" : "soon"}>{announced ? "Announced" : "Coming Soon"}</StatusPill>
            <h1 data-testid="team-name" className="mt-3 font-display text-5xl uppercase leading-none sm:text-6xl">{team.name}</h1>
            <p className="mt-2 text-sm uppercase tracking-[0.2em] text-slate-300">{team.city || "Regional identity to be announced"}</p>
            {team.owner_name && <p data-testid="team-owner" className="mt-2 text-sm text-slate-400">Owner: <span className="text-white">{team.owner_name}</span></p>}
          </div>
        </div>
      </section>
      <section className="container-x py-14">
        {!announced && <p className="mb-8 text-sm text-slate-400">This franchise has not yet been officially announced. Team identity, owner, squad and staff will appear here once confirmed.</p>}
        {team.description && <p className="mb-10 max-w-3xl text-slate-300">{team.description}</p>}
        <Tabs defaultValue="squad">
          <TabsList className="flex h-auto w-full flex-wrap justify-start gap-1 rounded-md bg-[#112240] p-1">
            {["squad", "staff", "fixtures", "results", "stats", "news", "gallery"].map((t) => (
              <TabsTrigger key={t} value={t} data-testid={`team-tab-${t}`} className="rounded-sm px-4 py-2 text-xs font-bold uppercase tracking-wider data-[state=active]:bg-[#00E5FF] data-[state=active]:text-[#0A192F]">{t}</TabsTrigger>
            ))}
          </TabsList>
          <TabsContent value="squad" className="mt-8">
            {players.length ? <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">{players.map((p) => <PlayerTile key={p.id} p={p} />)}</div>
              : <EmptyState testid="team-squad-empty" icon={Users} title="Squad to be announced">Player profiles appear here after official selection.</EmptyState>}
          </TabsContent>
          <TabsContent value="staff" className="mt-8">
            {coaches.length ? <ul className="grid gap-3 sm:grid-cols-2">{coaches.map((c) => <li key={c} className="rounded-md border border-white/10 bg-[#112240] p-4 font-semibold">{c}</li>)}</ul>
              : <EmptyState testid="team-staff-empty" icon={Shield} title="Coaching staff to be announced" />}
          </TabsContent>
          <TabsContent value="fixtures" className="mt-8">
            {upcoming.length ? <div className="grid gap-4 md:grid-cols-2">{upcoming.map((m) => <MatchCard key={m.id} match={m} teams={tMap} venues={vMap} />)}</div>
              : <EmptyState testid="team-fixtures-empty" icon={CalendarDays} title="Fixtures will be announced soon" />}
          </TabsContent>
          <TabsContent value="results" className="mt-8">
            {results.length ? <div className="grid gap-4 md:grid-cols-2">{results.map((m) => <MatchCard key={m.id} match={m} teams={tMap} venues={vMap} />)}</div>
              : <EmptyState testid="team-results-empty" icon={CalendarDays} title="No results yet" />}
          </TabsContent>
          <TabsContent value="stats" className="mt-8">
            <div data-testid="team-stats" className="grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-6">
              <StatBox l="Played" v={standing?.played} /><StatBox l="Won" v={standing?.won} /><StatBox l="Lost" v={standing?.lost} />
              <StatBox l="No result" v={standing?.no_result} /><StatBox l="Points" v={standing?.points} /><StatBox l="NRR" v={standing ? Number(standing.nrr).toFixed(3) : "0.000"} />
            </div>
          </TabsContent>
          <TabsContent value="news" className="mt-8">
            {news.length ? <div className="grid gap-6 md:grid-cols-3">{news.map((n) => <NewsCard key={n.id} article={n} />)}</div>
              : <EmptyState testid="team-news-empty" icon={Newspaper} title="No team news yet" />}
          </TabsContent>
          <TabsContent value="gallery" className="mt-8">
            {gallery.length ? <div className="grid grid-cols-2 gap-3 md:grid-cols-4">{gallery.map((g) => <img key={g.id} src={fileUrl(g.image)} alt={g.title} loading="lazy" className="aspect-square w-full rounded-md object-cover" />)}</div>
              : <EmptyState testid="team-gallery-empty" icon={ImageIcon} title="Gallery coming soon" />}
          </TabsContent>
        </Tabs>
      </section>
    </>
  );
}
